import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { ActivatedRoute, Router } from "@angular/router";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  LoadingDialogComponent,
} from "@cotecna/dialogs";
import {
  FormReturnType,
  NaturalFormConfig,
  NaturalFormFlavour,
  NaturalFormStyle,
} from "@cotecna/dynamic-view-engine";
import { TabsComponent } from "@cotecna/tabs";
import { Subscription } from "rxjs/internal/Subscription";
import {
  DynamicFormDialogComponent,
  DynamicFormDialogConfig,
  DynamicFormResult,
} from "src/app/form-dialog/components/natural-form-dialog/dynamic-form-dialog.component";
import { ToolbarOptionsHelper } from "src/app/inspection/details/toolbar-options-helper";
import { LoadingComponent } from "src/app/inspection/dialogs/loading/loading.component";
import { AssignedInspector } from "src/app/inspectors/models/assigned-inspector";
import { InspectorsService } from "src/app/inspectors/services/inspectors.service";
import { AssignedInspectorsQuery } from "src/app/inspectors/services/queries/assigned-inspectors.query";
import { User } from "src/app/session/models/user";
import { AppSettings } from "src/app/settings/models/app-settings";
import { AppSettingsService } from "src/app/settings/services/app-settings.service";
import { FeatureFlagsService } from "src/app/shared/services/feature-flags/feature-flags.service";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import {
  ADD_SERVICE_PROVIDER_COORDINATOR_ICON,
  ADD_SERVICE_PROVIDER_COORDINATOR_LABEL,
  ADD_SERVICE_PROVIDER_COORDINATOR_NAME,
  CLOSE_IO_ACTION_ICON,
  CLOSE_IO_ACTION_LABEL,
  CLOSE_IO_ACTION_NAME,
  FINISH_IO_ACTION_ICON,
  FINISH_IO_ACTION_LABEL,
  FINISH_IO_ACTION_NAME,
  InspectionListState,
  RE_ASSIGN_INSPECTION_ICON,
  RE_ASSIGN_INSPECTION_LABEL,
  RE_ASSIGN_INSPECTION_NAME,
  RE_ASSIGN_INSPECTION_TOOLTIP,
  REMOVE_SERVICE_PROVIDER_COORDINATOR_ICON,
  REMOVE_SERVICE_PROVIDER_COORDINATOR_LABEL,
  REMOVE_SERVICE_PROVIDER_COORDINATOR_NAME,
  REPORT_ISSUE_ACTION_NAME,
  REVIEW_COMMENTS_ICON,
  REVIEW_COMMENTS_LABEL,
  REVIEW_COMMENTS_NAME,
  REVIEW_COMMENTS_TOOLTIP,
  SEARCH_ACTION_ICON,
  SEARCH_ACTION_LABEL,
  SEARCH_ACTION_NAME,
  SEND_BY_EMAIL_ICON,
  SEND_BY_EMAIL_LABEL,
  SEND_BY_EMAIL_NAME,
  SEND_BY_EMAIL_TOOLTIP,
  STATUS_NEW,
} from "../../constants/constants";
import { FinishInspectionDialogComponent } from "../../dialogs/finish-inspection-dialog/finish-inspection-dialog.component";
import { LoadingDialogData } from "../../dialogs/loading/loading-dialog-data";
import { MissingRequiredFieldsComponent } from "../../dialogs/missing-required-fields/missing-required-fields.component";
import { MissingFieldsDialogData } from "../../dialogs/missing-required-fields/missing-required-fields-dialog-data";
import { ServiceProviderCoordinatorDialogComponent } from "../../dialogs/service-provider-coordinator-dialog/service-provider-coordinator-dialog.component";
import { ShareEmailDialogComponent } from "../../dialogs/share-email-dialog/share-email-dialog.component";
import {
  SearchDialogComponent,
  SearchDialogResult,
} from "../../inspections/search-component/search-dialog.component";
import { MultiSendByEmail } from "../../model/document-to-Share";
import { InspectionCommonData } from "../../model/inspection-common-data";
import { InspectionDocument } from "../../model/inspection-document";
import { InspectionState } from "../../model/inspection-state";
import { MissingField } from "../../model/missing-field";
import {
  RE_ASSIGN_INSPECTION_CONTROLS,
  RE_ASSIGN_INSPECTION_FORM,
} from "../../model/re-assign-inspection-form";
import {
  REVIEW_COMMENTS_CONTROLS,
  REVIEW_COMMENTS_FORM,
} from "../../model/review-comments-form";
import {
  SERVICE_PROVIDER_COORDINATOR_DIALOG_CONTROL,
  SERVICE_PROVIDER_COORDINATOR_DIALOG_FIELD,
} from "../../new-inspection/forms/service-provider-dialog-form";
import { PermissionsSetter } from "../../permissions-setter/permissions-setter";
import { InspectionDetailsService } from "../../services/inspection-details.service";
import { InspectionDocumentService } from "../../services/inspection-document-service/inspection-document.service";
import { InspectionListService } from "../../services/inspection-list.service";
import { PermissionsService } from "../../services/permissions.service";
import { ReportIssueService } from "../../services/report-issue.service";
import { SowDetailsService } from "../../services/sow-details-service/sow-details-service";
import {
  GENERATE_REPORT_ICON,
  GENERATE_REPORT_LABEL,
  GENERATE_REPORT_NAME,
  GENERATE_REPORT_TOOLTIP,
  REOPEN_ICON,
  REOPEN_LABEL,
  REOPEN_NAME,
  REOPEN_TOOLTIP,
} from "./../../constants/constants";
import { ReportsService } from "./../../services/inspection-report/inspection-report.service";
import { CustomTabsService } from "./custom-tabs.service";

export enum InspectionTabsName {
  generalInfo = "General Info",
  products = "Items",
  scopeOfWork = "Scope of Work",
  pictures = "Pictures",
  inspectors = "Inspectors",
  documents = "Documents",
  timelog = "Timelog",
  historyLog = "History Log",
  calculations = "Calculations",
}

export enum ToolbarType {
  inspectionToolbar = "inspectionToolbar",
  documentsToolbar = "documentsToolbar",
}

@Component({
  selector: "im-inspection-details-tabs",
  templateUrl: "./inspection-details-tabs.component.html",
  styleUrls: ["./inspection-details-tabs.component.scss"],
  providers: [SowDetailsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InspectionDetailsTabsComponent implements OnInit, OnDestroy {
  @ViewChild("dynamicTabs")
  public dynamicTabs: TabsComponent;

  public toolbarOptions;
  public toolbarType: ToolbarType = ToolbarType.inspectionToolbar;
  public inspectionTabs = [];
  public status: string;
  public permissions: PermissionsSetter;
  public isAccountTypeManagementOnly: boolean = false;
  public isInspector: boolean = false;
  public hasScopeOfWork: boolean = false;

  protected scopeOfWorkDataSubscription: Subscription;
  protected inspectionChangeSubscription: Subscription;

  private appSettings: AppSettings = null;
  private tabsInspectionSubscription: Subscription;
  private sendMultipleDocumentsByEmailSubscription: Subscription;
  private inspectionId: string;
  private inspectionState: InspectionState;
  private hasInspectors: boolean;
  private inspectionName: string;
  private assignedServiceProviderCoordinators: User[];
  private availableServiceProviderCoordinators: User[];
  private documentsToShareByEmail: InspectionDocument[];

  private readonly goBackTab: InspectionListState = InspectionListState.Active;

  private formConfig: NaturalFormConfig = {
    displayMode: NaturalFormStyle.material,
    flavor: NaturalFormFlavour.informative,
    notifyFieldChange: false,
    returnType: FormReturnType.entity,
    readonly: false,
  };

  private readonly generateSearchAction = {
    icon: SEARCH_ACTION_ICON,
    label: SEARCH_ACTION_LABEL,
    name: SEARCH_ACTION_NAME,
  };

  private readonly generateReportAction = {
    icon: GENERATE_REPORT_ICON,
    label: GENERATE_REPORT_LABEL,
    name: GENERATE_REPORT_NAME,
    tooltip: GENERATE_REPORT_TOOLTIP,
  };

  private readonly addServiceProviderCoordinatorAction = {
    icon: ADD_SERVICE_PROVIDER_COORDINATOR_ICON,
    label: ADD_SERVICE_PROVIDER_COORDINATOR_LABEL,
    name: ADD_SERVICE_PROVIDER_COORDINATOR_NAME,
  };

  private readonly removeServiceProviderCoordinatorAction = {
    icon: REMOVE_SERVICE_PROVIDER_COORDINATOR_ICON,
    label: REMOVE_SERVICE_PROVIDER_COORDINATOR_LABEL,
    name: REMOVE_SERVICE_PROVIDER_COORDINATOR_NAME,
  };

  private readonly reopenAction = {
    icon: REOPEN_ICON,
    label: REOPEN_LABEL,
    name: REOPEN_NAME,
    tooltip: REOPEN_TOOLTIP,
  };

  private readonly multiSendByEmailAction = {
    icon: SEND_BY_EMAIL_ICON,
    label: SEND_BY_EMAIL_LABEL,
    name: SEND_BY_EMAIL_NAME,
    tooltip: SEND_BY_EMAIL_TOOLTIP,
  };

  private readonly reviewComments = {
    icon: REVIEW_COMMENTS_ICON,
    label: REVIEW_COMMENTS_LABEL,
    name: REVIEW_COMMENTS_NAME,
    tooltip: REVIEW_COMMENTS_TOOLTIP,
  };

  private readonly reAssignInspection = {
    icon: RE_ASSIGN_INSPECTION_ICON,
    label: RE_ASSIGN_INSPECTION_LABEL,
    name: RE_ASSIGN_INSPECTION_NAME,
    tooltip: RE_ASSIGN_INSPECTION_TOOLTIP,
  };

  constructor(
    private inspectionListService: InspectionListService,
    private featureFlagsService: FeatureFlagsService,
    private toolbarOptionsHelper: ToolbarOptionsHelper,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private inspectionDetailService: InspectionDetailsService,
    private inspectorsService: InspectorsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private inspectionReportService: ReportsService,
    private permissionsService: PermissionsService,
    private customTabsService: CustomTabsService,
    private userRolePermissionsService: UserRolePermissionsService,
    private reportIssueService: ReportIssueService,
    private appSettingsService: AppSettingsService,
    private inspectionDocumentService: InspectionDocumentService,
    private el: ElementRef,
    private renderer: Renderer2,
    private documentService: InspectionDocumentService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.toolbarOptions = this.toolbarOptionsHelper.toolbarOptions;

    const navigation = this.router.getCurrentNavigation();
    this.goBackTab = navigation?.extras?.state?.activeTabState;
    if (!this.goBackTab) {
      this.goBackTab = InspectionListState.Active;
    }
  }

  public async ngOnInit(): Promise<void> {
    this.getAccountType();
    this.appSettings = await this.appSettingsService.get();
    if (
      !this.userRolePermissionsService.isInspector() &&
      !this.userRolePermissionsService.isServiceProviderInspector()
    )
      this.availableServiceProviderCoordinators =
        await this.inspectorsService.getServiceProviderCoordinators();
    this.trySetReportIssueButton();

    this.inspectionId = this.activatedRoute.snapshot.paramMap.get("id");
    this.permissions = await this.permissionsService.getPermissions();

    await this.getInspectionCommonData();
    await this.defineInspectionTabs();
    this.setSelectedTab();
    this.toolbarOptions = { ...this.toolbarOptionsHelper.toolbarOptions };
    this.changeDetectorRef.markForCheck();

    this.inspectionChangeSubscription =
      this.inspectionDetailService.inspectionChanges.subscribe((result) => {
        if (result) {
          this.status = InspectionState.getInspectionState(result.state);
          this.getInspectionCommonData();
        }
      });

    this.sendMultipleDocumentsByEmailSubscription =
      this.inspectionDocumentService.documentsToShareByEmail.subscribe(
        async (documentsToShareByEmail) => {
          if (documentsToShareByEmail?.length > 0) {
            this.documentsToShareByEmail = documentsToShareByEmail;
            await this.transformToolbarToShareDocumentsByEmail(
              documentsToShareByEmail?.length,
            );
          } else if (documentsToShareByEmail?.length == 0) {
            if (this.toolbarType != ToolbarType.inspectionToolbar)
              await this.manageInspectionToolbar();
          }
        },
      );
  }

  public ngOnDestroy(): void {
    this.inspectionDetailService.resetInspection();
    this.tabsInspectionSubscription?.unsubscribe();
    this.scopeOfWorkDataSubscription?.unsubscribe();
    this.inspectionChangeSubscription?.unsubscribe();
    this.sendMultipleDocumentsByEmailSubscription?.unsubscribe();
  }

  private getAccountType(): void {
    this.isAccountTypeManagementOnly =
      this.userRolePermissionsService.isTimeManagementOnlyAccount();
    this.isInspector = this.userRolePermissionsService.isInspector();
  }

  private inspectionCanBeClosed(state: InspectionState) {
    return state != InspectionState.Closed && state != InspectionState.New;
  }

  async onTabChanged(event: MatTabChangeEvent) {
    if (event) {
      const tabPath = this.inspectionTabs[event.index]?.path;
      if (!tabPath || this.router.url.endsWith(`/${tabPath}`)) {
        return;
      }
      this.router.navigate([tabPath], {
        relativeTo: this.activatedRoute,
      });
      if (
        tabPath != "documents" &&
        this.toolbarType != ToolbarType.inspectionToolbar
      ) {
        await this.manageInspectionToolbar();
      }
    }
  }

  public async onToolbarCloseActionClicked(): Promise<void> {
    await this.manageInspectionToolbar();
  }

  public async onToolbarActionClicked(action: string): Promise<void> {
    switch (action) {
      case CLOSE_IO_ACTION_NAME:
      case FINISH_IO_ACTION_NAME:
        await this.performFinishInspection();
        break;
      case GENERATE_REPORT_NAME:
        await this.generateReport();
        break;
      case REOPEN_NAME:
        await this.reopenInspection();
        break;
      case REPORT_ISSUE_ACTION_NAME:
        await this.reportIssue();
        break;
      case ADD_SERVICE_PROVIDER_COORDINATOR_NAME:
        await this.openServiceProviderCoordinatorDialog();
        break;
      case REMOVE_SERVICE_PROVIDER_COORDINATOR_NAME:
        await this.unassingServiceProviderCoordinator();
        break;
      case SEARCH_ACTION_NAME:
        await this.search();
        break;
      case SEND_BY_EMAIL_NAME:
        await this.sendMultipleDocumentsByEmail();
        break;
      case REVIEW_COMMENTS_NAME:
        await this.openReviewCommentsDialog();
        break;
      case RE_ASSIGN_INSPECTION_NAME:
        await this.openReAssignInspectionDialog();
        break;
    }
  }

  private async search(): Promise<void> {
    const searchDialog = this.dialog.open(SearchDialogComponent, {
      panelClass: "dialog",
      autoFocus: false,
    });

    const result: SearchDialogResult = await searchDialog
      .afterClosed()
      .toPromise();
    if (result?.action) {
      this.inspectionListService.search(result.searchText, result.searchType);
    }
  }

  public async defineInspectionTabs(): Promise<void> {
    this.inspectionTabs.push({
      name: InspectionTabsName.generalInfo,
      icon: "info_outline",
      path: "info",
    });

    if (!this.isAccountTypeManagementOnly) {
      this.inspectionTabs.push({
        name: InspectionTabsName.products,
        icon: "widgets",
        path: "items",
      });
    }

    const state = InspectionState.getInspectionState(this.inspectionState);
    if (
      state != STATUS_NEW &&
      this.hasScopeOfWork &&
      !this.isAccountTypeManagementOnly
    ) {
      this.inspectionTabs.push({
        name: InspectionTabsName.scopeOfWork,
        icon: "assignment",
        path: "sow",
      });
    }

    if (!this.isAccountTypeManagementOnly) {
      this.inspectionTabs.push({
        name: InspectionTabsName.pictures,
        icon: "collections",
        path: "photos",
      });
    }

    if (this.featureFlagsService.isTimelogTabEnabled()) {
      if (this.isAccountTypeManagementOnly) {
        if (this.userRolePermissionsService.hasInspectorRole()) {
          this.inspectionTabs.push({
            name: InspectionTabsName.timelog,
            icon: "event_note",
            path: "timelog",
          });
        }
      } else {
        this.inspectionTabs.push({
          name: InspectionTabsName.timelog,
          icon: "event_note",
          path: "timelog",
        });
      }
    }

    if (!this.isAccountTypeManagementOnly) {
      this.inspectionTabs.push({
        name: InspectionTabsName.documents,
        icon: "file_copy",
        path: "documents",
      });
    }

    if (
      !this.userRolePermissionsService.isInspector() &&
      !this.userRolePermissionsService.isReadOnly() &&
      !this.userRolePermissionsService.isServiceProviderInspector() &&
      (!this.isAccountTypeManagementOnly || !this.isInspector)
    ) {
      this.inspectionTabs.push({
        name: InspectionTabsName.inspectors,
        icon: "supervisor_account",
        path: "inspectors",
      });
    }

    if (
      this.featureFlagsService.allowHistoryLogTab() &&
      this.userRolePermissionsService.canAccessToHistoryLog() &&
      (!this.isAccountTypeManagementOnly || !this.isInspector)
    ) {
      this.inspectionTabs.push({
        name: InspectionTabsName.historyLog,
        icon: "history",
        path: "history",
      });
    }

    const customTab = await this.customTabsService.getCustomTab();
    if (customTab)
      this.inspectionTabs.splice(customTab.position, 0, customTab.tab);
  }

  private async getInspectionCommonData() {
    try {
      const inspectionData: InspectionCommonData =
        await this.inspectionDetailService.getInspectionCommonData(
          this.inspectionId,
        );
      const assignedInspectors: AssignedInspector[] =
        await this.inspectorsService.getAssignedInspectors({
          inspectionId: this.inspectionId,
        });
      this.inspectionState = inspectionData?.state;
      this.inspectionName = inspectionData?.name;
      this.hasInspectors = !!assignedInspectors?.length;
      this.hasScopeOfWork = inspectionData?.hasScopeOfWork;
      await this.manageInspectionToolbar();
    } catch (err) {
      throw err;
    }
  }

  public async manageInspectionToolbar(): Promise<void> {
    this.toolbarType = ToolbarType.inspectionToolbar;
    this.toolbarOptionsHelper.setBackFunction(() => {
      this.navigateBackToInspections();
    });
    const toolbar = this.el.nativeElement.querySelector(
      ".mat-toolbar.mat-primary",
    );
    if (toolbar) {
      this.renderer.removeClass(toolbar, "email-toolbar-theme");
    }
    this.toolbarOptionsHelper.clearMainActions();

    if (this.userRolePermissionsService.canAccessToInspectionsList) {
      this.toolbarOptionsHelper.addAction(this.generateSearchAction);
    }

    this.toolbarOptionsHelper.setTitle(`${this.inspectionName}`);
    this.assignedServiceProviderCoordinators =
      await this.inspectionDetailService.getAssignedServiceProviderCoordinators(
        this.inspectionId,
      );
    if (this.userRolePermissionsService.canCreate()) {
      if (
        this.inspectionState == InspectionState.Finished ||
        this.inspectionState == InspectionState.PartiallyFinished
      ) {
        this.toolbarOptionsHelper.addAction(this.reAssignInspection);
        this.toolbarOptionsHelper.addAction(this.reviewComments);
      }
      if (this.assignedServiceProviderCoordinators?.length)
        this.toolbarOptionsHelper.addAction(
          this.removeServiceProviderCoordinatorAction,
        );
      else
        this.toolbarOptionsHelper.addAction(
          this.addServiceProviderCoordinatorAction,
        );
    }
    this.toolbarOptionsHelper.addAction(this.generateReportAction);
    this.status = InspectionState.getInspectionState(this.inspectionState);
    if (
      !this.userRolePermissionsService.isInspector() ||
      !this.userRolePermissionsService.isServiceProviderInspector()
    ) {
      if (
        this.inspectionCanBeClosed(this.inspectionState) &&
        !this.permissions.setScopeOfWorkConfig()?.isReadOnly
      ) {
        if (this.inspectionState == InspectionState.Finished) {
          this.toolbarOptionsHelper.addAction({
            name: CLOSE_IO_ACTION_NAME,
            label: CLOSE_IO_ACTION_LABEL,
            icon: CLOSE_IO_ACTION_ICON,
          });
        } else {
          this.toolbarOptionsHelper.addAction({
            name: FINISH_IO_ACTION_NAME,
            label: FINISH_IO_ACTION_LABEL,
            icon: FINISH_IO_ACTION_ICON,
          });
        }
      }
      if (this.inspectionState == InspectionState.Closed) {
        this.showReopenAction();
      } else {
        if (
          this.inspectionCanBeFinished(this.inspectionState) &&
          this.userRolePermissionsService.isInspector() &&
          !this.toolbarOptionsHelper
            .getCurrentActions()
            .some((a) => a.name === FINISH_IO_ACTION_NAME)
        ) {
          this.toolbarOptionsHelper.addAction({
            name: FINISH_IO_ACTION_NAME,
            label: FINISH_IO_ACTION_LABEL,
            icon: FINISH_IO_ACTION_ICON,
          });
        }
        const actions = this.toolbarOptionsHelper
          .getCurrentActions()
          .filter((a) => a.name != REOPEN_NAME);
        this.toolbarOptionsHelper.setActions(actions);
      }
    } else {
      const actions = this.toolbarOptionsHelper
        .getCurrentActions()
        .filter((a) => a.name != REOPEN_NAME);
      this.toolbarOptionsHelper.setActions(actions);
      if (
        (this.inspectionCanBeFinished(this.inspectionState) ||
          !this.hasInspectors) &&
        !this.permissions.setScopeOfWorkConfig().isReadOnly
      ) {
        this.toolbarOptionsHelper.addAction({
          name: FINISH_IO_ACTION_NAME,
          label: FINISH_IO_ACTION_LABEL,
          icon: FINISH_IO_ACTION_ICON,
        });
      }
    }

    this.trySetReportIssueButton();
    this.toolbarOptions = {
      ...this.toolbarOptionsHelper.toolbarOptions,
      mainActions: [...this.toolbarOptionsHelper.toolbarOptions.mainActions],
      moreActions: [...this.toolbarOptionsHelper.toolbarOptions.moreActions],
    };
    this.changeDetectorRef.markForCheck();
  }

  private async transformToolbarToShareDocumentsByEmail(
    numberOfDocumentsToShareByEmail: number,
  ) {
    this.toolbarType = ToolbarType.documentsToolbar;
    this.toolbarOptionsHelper.clear();
    this.status = null;
    this.toolbarOptionsHelper.setBackFunction(async () => {
      await this.restartCheckedDocuments();
    });
    const toolbar = this.el.nativeElement.querySelector(
      ".mat-toolbar.mat-primary",
    );
    this.renderer.addClass(toolbar, "email-toolbar-theme");
    this.toolbarOptionsHelper.setTitle(
      `${numberOfDocumentsToShareByEmail} Selected`,
    );
    this.toolbarOptionsHelper.addAction(this.multiSendByEmailAction);
    this.toolbarOptions = {
      ...this.toolbarOptionsHelper.toolbarOptions,
      mainActions: [...this.toolbarOptionsHelper.toolbarOptions.mainActions],
      moreActions: [...this.toolbarOptionsHelper.toolbarOptions.moreActions],
    };
    this.changeDetectorRef.markForCheck();
  }

  private async restartCheckedDocuments(): Promise<void> {
    this.documentService.restartCheckedDocuments();
  }

  private inspectionCanBeFinished(state: InspectionState): boolean {
    return (
      state == InspectionState.Open ||
      state == InspectionState.PartiallyFinished
    );
  }

  private async reopenInspection() {
    let loadingDialogRef: MatDialogRef<any> = null;
    try {
      loadingDialogRef = this.openLoadingDialog("Reopening inspection");
      await this.inspectionDetailService.reopenInspection(this.inspectionId);
      this.inspectionState = InspectionState.Finished;
      await this.getInspectionCommonData();
    } finally {
      loadingDialogRef.close();
    }
  }

  private async generateReport() {
    const loadingDialogRef = this.dialog.open(LoadingDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: "Generating Report...",
    });
    await this.inspectionReportService
      .generateReport(this.inspectionId, false)
      .then((_) =>
        this.inspectionReportService.inspectionReportGenerated.next(true),
      )
      .finally(() => loadingDialogRef.close());
  }

  private async openServiceProviderCoordinatorDialog(): Promise<void> {
    const serviceProviderCoordinatorSection = structuredClone(
      SERVICE_PROVIDER_COORDINATOR_DIALOG_FIELD,
    );
    const serviceProviderCoordinatorControl = structuredClone(
      SERVICE_PROVIDER_COORDINATOR_DIALOG_CONTROL,
    );

    this.availableServiceProviderCoordinators.forEach((c) => {
      serviceProviderCoordinatorControl.options.push({
        id: c.email,
        name: c.email,
      });
    });

    const dialogRef = this.dialog.open(
      ServiceProviderCoordinatorDialogComponent,
      {
        data: {
          section: serviceProviderCoordinatorSection,
          control: serviceProviderCoordinatorControl,
        },
        autoFocus: false,
        disableClose: false,
      },
    );

    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      await this.inspectionDetailService.assignServiceProviderCoordinators(
        this.inspectionId,
        result.data.serviceProviderCoordinators,
      );
      window.location.reload();
    }
  }

  private async unassingServiceProviderCoordinator(): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: <ConfirmDialogData>{
        icon: REMOVE_SERVICE_PROVIDER_COORDINATOR_ICON,
        okButton: {
          text: "UNASSIGN",
          color: "warn",
        },
        cancelButton: {
          text: "CANCEL",
          color: "primary",
        },
        title: "Remove External Assignation?",
        message: `This inspection access is going to be removed for external provider account users.\n\nOnce removed, they are not going to be able to view or assign inspectors. Do you want to proceed?`,
      },
    });
    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      await this.inspectionDetailService.unassignServiceProviderCoordinators(
        this.inspectionId,
      );
      window.location.reload();
    }
  }

  private showReopenAction() {
    if (!this.permissions.setScopeOfWorkConfig().isReadOnly) {
      this.toolbarOptionsHelper.addAction(this.reopenAction);
    }
  }

  private async openFinishIODialog(): Promise<void> {
    let finishInspectionDialog: MatDialogRef<any> = null;
    let loadingDialog: MatDialogRef<any> = null;
    try {
      finishInspectionDialog = this.openFinishInspectionDialog(
        this.hasInspectors,
      );
      const result = await finishInspectionDialog?.afterClosed().toPromise();
      if (result) {
        loadingDialog = this.dialog.open(LoadingDialogComponent, {
          autoFocus: false,
          disableClose: true,
          data: "Finishing Inspection, please wait...",
        });
        await this.finishInspection(result.inspectionResult);
        this.inspectionState = InspectionState.Finished;
        await this.manageInspectionToolbar();
        this.snackBar.open(
          "The inspection has been finished successfully",
          "",
          { duration: 3000, panelClass: ["success-snackbar"] },
        );
        this.inspectorsService.invokeQuery(
          new AssignedInspectorsQuery(this.inspectionId),
        );
        this.inspectionDetailService.notifyAboutChange();
      }
    } finally {
      finishInspectionDialog?.close();
      loadingDialog?.close();
    }
  }

  private async performFinishInspection(): Promise<void> {
    let loadingDialog: MatDialogRef<any> = null;
    try {
      loadingDialog = this.openLoadingDialog("Validating inspection data");
      const missingRequiredFields =
        await this.inspectionDetailService.getMissingRequiredFields(
          this.inspectionId,
        );
      loadingDialog?.close();
      if (missingRequiredFields?.length)
        this.openMissingFieldsDialog(missingRequiredFields);
      else {
        switch (this.inspectionState) {
          case InspectionState.Open:
          case InspectionState.PartiallyFinished:
            await this.openFinishIODialog();
            break;
          case InspectionState.Finished:
            await this.closeInspection();
            break;
        }
      }
    } finally {
      loadingDialog?.close();
    }
  }

  private async sendMultipleDocumentsByEmail() {
    const dialogRef = this.dialog.open(ShareEmailDialogComponent, {
      data: {
        defaultEmails: [],
        allowAdditionalEmails: true,
      },
      panelClass: "dialog",
      width: "50vw",
      height: "40vh",
      autoFocus: false,
    });

    const result = await dialogRef?.afterClosed().toPromise();
    if (result) {
      const multiSendByEmail: MultiSendByEmail = {
        inspectionId: this.inspectionId,
        documentsToSend: [],
        destinationEmails: result.emailsToSend,
      };
      this.documentsToShareByEmail.forEach((document) => {
        multiSendByEmail.documentsToSend.push({
          documentId: document.documentId,
          documentName: document.name,
        });
      });
      await this.restartCheckedDocuments();
      await this.documentService.sendByEmail(multiSendByEmail);
    }
  }

  private async openReviewCommentsDialog(): Promise<void> {
    const dialogRef = this.dialog.open(DynamicFormDialogComponent, {
      data: {
        icon: REVIEW_COMMENTS_ICON,
        title: "Add Review Comments/Remarks",
        formConfig: this.formConfig,
        sections: JSON.parse(JSON.stringify(REVIEW_COMMENTS_FORM)),
        controls: JSON.parse(JSON.stringify(REVIEW_COMMENTS_CONTROLS)),
        confirmButtonText: "CONFIRM",
        cancelButtonText: "CANCEL",
        allowConfirmWithInvalidFrom: false,
      } as DynamicFormDialogConfig,
      height: "530px",
      width: "800px",
      autoFocus: true,
      disableClose: false,
      panelClass: "dynamic-form-dialog",
    });

    await dialogRef
      .afterClosed()
      .toPromise()
      .then(async (result: DynamicFormResult) => {
        if (result) {
          await this.inspectionDetailService.sendComments(
            this.inspectionId,
            result.data.comments,
          );
          this.snackBar.open(
            "Review Comment added on the Inspection History Log",
            "CLOSE",
            { duration: 3000, panelClass: ["success-snackbar"] },
          );
        }
      });
  }

  private async openReAssignInspectionDialog(): Promise<void> {
    const finisInspectors =
      await this.inspectionDetailService.getFinishInspectors(this.inspectionId);
    const controls = structuredClone(RE_ASSIGN_INSPECTION_CONTROLS);
    finisInspectors?.forEach((inspector) => {
      controls
        .find((controls) => controls.id == "assignedInspector")
        .options.push({ id: inspector.userId, name: inspector.name });
    });
    const dialogRef = this.dialog.open(DynamicFormDialogComponent, {
      data: {
        icon: RE_ASSIGN_INSPECTION_ICON,
        title: RE_ASSIGN_INSPECTION_LABEL,
        formConfig: this.formConfig,
        sections: JSON.parse(JSON.stringify(RE_ASSIGN_INSPECTION_FORM)),
        controls: controls,
        confirmButtonText: "CONFIRM",
        cancelButtonText: "CANCEL",
        allowConfirmWithInvalidFrom: false,
        fileUploader: true,
      } as DynamicFormDialogConfig,
      height: "800px",
      width: "650px",
      autoFocus: true,
      disableClose: false,
      panelClass: "dynamic-form-dialog",
    });

    await dialogRef
      .afterClosed()
      .toPromise()
      .then(async (result: DynamicFormResult) => {
        if (result) {
          const loadingDialog = this.openLoadingDialog(
            "Re-assigning inspection",
          );
          await this.inspectionDetailService.reAssignInspectors(
            this.inspectionId,
            result.data.assignedInspector,
            result.data.comments,
            result.file ? result.file[0] : null,
          );
          this.snackBar.open("Inspector re-assigned", "CLOSE", {
            duration: 3000,
            panelClass: ["success-snackbar"],
          });
          this.inspectionDetailService.fillInspection(this.inspectionId);
          this.inspectorsService.invokeQuery(
            new AssignedInspectorsQuery(this.inspectionId),
          );
          loadingDialog?.close();
        }
      });
  }

  private setSelectedTab(): void {
    this.inspectionTabs.forEach((tab: any, tabIndex: number) => {
      if (this.router.url.includes(tab.path)) {
        this.dynamicTabs.setSelectedTab(tabIndex);
        return;
      }
    });
  }

  private async finishInspection(inspectionResult: string) {
    await this.inspectionDetailService.finishInspection(
      this.inspectionId,
      inspectionResult,
    );
  }

  private async closeInspection(): Promise<void> {
    let loadingDialog: MatDialogRef<any>;
    try {
      const result = await this.openCloseInspectionDialog(true)
        .afterClosed()
        .toPromise();
      if (result) {
        loadingDialog = this.dialog.open(LoadingDialogComponent, {
          autoFocus: false,
          disableClose: true,
          data: "Closing Inspection, please wait...",
        });

        await this.inspectionDetailService.closeInspection(this.inspectionId);
        this.snackBar.open("The inspection has been closed successfully", "", {
          duration: 3000,
          data: { color: "primary" },
        });
        this.inspectionState = InspectionState.Closed;
        await this.getInspectionCommonData();
      }
    } finally {
      loadingDialog?.close();
    }
  }

  private navigateBackToInspections(): void {
    this.toolbarOptionsHelper.clear();
    this.router.navigate(["inspections"], {
      state: { activeTabState: this.goBackTab },
    });
  }

  private openMissingFieldsDialog(missingRequiredFields: MissingField[]) {
    this.dialog.open(MissingRequiredFieldsComponent, {
      data: {
        missingRequiredFields: missingRequiredFields,
      } as MissingFieldsDialogData,
      panelClass: "missing-fields-dialog",
      autoFocus: false,
      disableClose: true,
    });
  }

  private openCloseInspectionDialog(
    isPossibleToClose: boolean,
  ): MatDialogRef<ConfirmDialogComponent> {
    return this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: isPossibleToClose ? "warning" : "list",
        okButton: {
          text: isPossibleToClose ? "Accept" : "Cancel",
          color: "primary",
        },
        cancelButton: {
          text: isPossibleToClose ? "Cancel" : "",
        },
        title: isPossibleToClose ? "Close inspection" : "",
        message: isPossibleToClose
          ? 'Do you want to set this inspection as "Closed"?'
          : "",
      },
      autoFocus: false,
    });
  }

  private openFinishInspectionDialog(
    hasInspectors: boolean,
  ): MatDialogRef<FinishInspectionDialogComponent> {
    return this.dialog.open(FinishInspectionDialogComponent, {
      data: { hasInspectors: hasInspectors },
      autoFocus: false,
    });
  }

  private openLoadingDialog(message: string): MatDialogRef<any> {
    return this.dialog.open(LoadingComponent, {
      data: { loadingText: message } as LoadingDialogData,
      autoFocus: false,
      disableClose: false,
    });
  }

  private trySetReportIssueButton(): void {
    if (
      this.appSettings?.ticketingReportingEnabled &&
      this.permissionsService.isCurrentUserInternal()
    )
      this.toolbarOptionsHelper.tryAddReportIssueAction();
  }

  private async reportIssue(): Promise<void> {
    this.reportIssueService.openDialog();
  }
}
